import {
  AutocompleteInputComponent,
  AutocompleteItem,
} from '@delmonicos/shared-design-system-react/src/components/autocomplete-input/autocomplete-input.component';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import {
  Text,
  TextFontSize,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { TicketRelatedResourceType } from '@delmonicos/shared-sdk/libs/cpms/src/libs/support-service-lib/src/wrapper/enums/ticket-related-resource-type.enum';
import { CreateTicketModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/support-service-lib/src/wrapper/inputs/create-ticket.model';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useCpmsBoGetChargePointsPaginatedQuery } from '@service/cpms-bo-charge-point.service';
import { useCpmsBoGetCustomersPaginatedQuery } from '@service/cpms-bo-customer.service';
import { useCpmsBoGetLocationsPaginatedQuery } from '@service/cpms-bo-location.service';
import { useCpmsBoGetNetworksPaginatedQuery } from '@service/cpms-bo-network.service';

interface SelectedResource {
  id: string;
  name: string;
}

export const RelatedResourcesSectionController = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { setValue } = useFormContext<CreateTicketModel>();

  const [customer, setCustomer] = useState<SelectedResource | null>(null);
  const [network, setNetwork] = useState<SelectedResource | null>(null);
  const [location, setLocation] = useState<SelectedResource | null>(null);
  const [chargePoint, setChargePoint] = useState<SelectedResource | null>(null);

  const [customerSearch, setCustomerSearch] = useState('');
  const [networkSearch, setNetworkSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [chargePointSearch, setChargePointSearch] = useState('');

  const [customerItems, setCustomerItems] = useState<AutocompleteItem[]>([]);
  const [networkItems, setNetworkItems] = useState<AutocompleteItem[]>([]);
  const [locationItems, setLocationItems] = useState<AutocompleteItem[]>([]);
  const [chargePointItems, setChargePointItems] = useState<AutocompleteItem[]>(
    [],
  );

  const { data: customersData, isSuccess: customersSuccess } =
    useCpmsBoGetCustomersPaginatedQuery({
      limit: 10,
      offset: 0,
      name: customerSearch === '' ? undefined : `~${customerSearch}`,
    });

  const { data: networksData, isSuccess: networksSuccess } =
    useCpmsBoGetNetworksPaginatedQuery(
      {
        limit: 10,
        offset: 0,
        customerId: customer?.id,
        name: networkSearch === '' ? undefined : `~${networkSearch}`,
      },
      { skip: !customer },
    );

  const { data: locationsData, isSuccess: locationsSuccess } =
    useCpmsBoGetLocationsPaginatedQuery(
      {
        limit: 10,
        offset: 0,
        networkId: network?.id,
        name: locationSearch === '' ? undefined : `~${locationSearch}`,
      },
      { skip: !network },
    );

  const { data: chargePointsData, isSuccess: chargePointsSuccess } =
    useCpmsBoGetChargePointsPaginatedQuery(
      {
        limit: 10,
        offset: 0,
        locationId: location?.id,
        name: chargePointSearch === '' ? undefined : `~${chargePointSearch}`,
      },
      { skip: !location },
    );

  useEffect(() => {
    if (customersSuccess) {
      setCustomerItems(
        customersData.docs.map(({ id, name }) => ({ key: id, label: name })),
      );
    }
  }, [customersData, customersSuccess]);

  useEffect(() => {
    if (networksSuccess) {
      setNetworkItems(
        networksData.docs.map(({ id, name }) => ({ key: id, label: name })),
      );
    }
  }, [networksData, networksSuccess]);

  useEffect(() => {
    if (locationsSuccess) {
      setLocationItems(
        locationsData.docs.map(({ id, name }) => ({ key: id, label: name })),
      );
    }
  }, [locationsData, locationsSuccess]);

  useEffect(() => {
    if (chargePointsSuccess) {
      setChargePointItems(
        chargePointsData.docs.map(({ id, name }) => ({
          key: id,
          label: name,
        })),
      );
    }
  }, [chargePointsData, chargePointsSuccess]);

  useEffect(() => {
    if (chargePoint) {
      setValue('relatedResources', [
        { id: chargePoint.id, type: TicketRelatedResourceType.ChargePoint },
      ]);
      return;
    }
    if (location) {
      setValue('relatedResources', [
        { id: location.id, type: TicketRelatedResourceType.Location },
      ]);
      return;
    }
    setValue('relatedResources', []);
  }, [location, chargePoint, setValue]);

  useEffect(() => {
    setValue('customerId', customer?.id ?? '');
    setValue('networkId', network?.id ?? '');
    setValue('assigneeId', undefined);
  }, [customer, network, setValue]);

  return (
    <Stack direction="column" gap={GapEnum.M}>
      <Text uppercase fontSize={TextFontSize.Small}>
        {t('$.ticket-related-resources.title')}
      </Text>

      <Stack gap={GapEnum.S} flex={1}>
        <AutocompleteInputComponent
          placeholder={t('$.ticket-related-resources.customer-placeholder')}
          value={customerSearch}
          onChange={setCustomerSearch}
          onSearch={setCustomerSearch}
          items={customerItems}
          selectedItem={
            customer ? { key: customer.id, label: customer.name } : null
          }
          onSelect={(item) => {
            setCustomer(item ? { id: item.key, name: item.label } : null);
            setNetwork(null);
            setLocation(null);
            setChargePoint(null);
            setNetworkItems([]);
            setLocationItems([]);
            setChargePointItems([]);
            setNetworkSearch('');
            setLocationSearch('');
            setChargePointSearch('');
          }}
          emptyResultText={t('global.no-result-found')}
        />

        <AutocompleteInputComponent
          placeholder={t('$.ticket-related-resources.network-placeholder')}
          disabled={!customer}
          value={networkSearch}
          onChange={setNetworkSearch}
          onSearch={setNetworkSearch}
          items={networkItems}
          selectedItem={
            network ? { key: network.id, label: network.name } : null
          }
          onSelect={(item) => {
            setNetwork(item ? { id: item.key, name: item.label } : null);
            setLocation(null);
            setChargePoint(null);
            setLocationItems([]);
            setChargePointItems([]);
            setLocationSearch('');
            setChargePointSearch('');
          }}
          emptyResultText={t('global.no-result-found')}
        />

        <AutocompleteInputComponent
          placeholder={t('$.ticket-related-resources.location-placeholder')}
          disabled={!network}
          value={locationSearch}
          onChange={setLocationSearch}
          onSearch={setLocationSearch}
          items={locationItems}
          selectedItem={
            location ? { key: location.id, label: location.name } : null
          }
          onSelect={(item) => {
            setLocation(item ? { id: item.key, name: item.label } : null);
            setChargePoint(null);
            setChargePointItems([]);
            setChargePointSearch('');
          }}
          emptyResultText={t('global.no-result-found')}
        />

        <AutocompleteInputComponent
          placeholder={t('$.ticket-related-resources.charge-point-placeholder')}
          disabled={!location}
          value={chargePointSearch}
          onChange={setChargePointSearch}
          onSearch={setChargePointSearch}
          items={chargePointItems}
          selectedItem={
            chargePoint
              ? { key: chargePoint.id, label: chargePoint.name }
              : null
          }
          onSelect={(item) => {
            setChargePoint(item ? { id: item.key, name: item.label } : null);
          }}
          emptyResultText={t('global.no-result-found')}
        />
      </Stack>
    </Stack>
  );
};
